import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  makeStyles,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import DataTable, { TableColumn } from "react-data-table-component";
import { tableCustomStyles } from "../../utils/table-styles";
import PaginatedTableTop, { paginationTopClassName } from "../shared/paginated-table-top";
import DailyMetrics from "../shared/daily-metrics";
import TicketRangesPanel from "../shared/ticket-ranges-panel";
import { ApiResponse } from "apisauce";
import { toast } from "react-toastify";
import Tooltip from "@material-ui/core/Tooltip";
import InfoIcon from "@material-ui/icons/Info";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import ReceiptIcon from "@material-ui/icons/Receipt";
import BusinessIcon from "@material-ui/icons/Business";
import { debounce } from "lodash";
import { Formik } from "formik";

import {
  useAppDispatch,
  useAppSelector,
  useTicketRangeLimits,
  shouldConfirmBatch,
} from "../../hooks";
import { BulkOperationMaxRange } from "../../utils/constants";
import { getCurrentUser } from "../../store/userStore";
import {
  getSmallQuantityTickets,
  setSmallQuantityTickets,
} from "../../store/smallQuantityTicketStore";
import { SmallQuantityTicket } from "../../interface/api/SmallQuantityTicket";
import { Stockpile } from "../../interface/api/Stockpile";
import { SearchPayload } from "../../interface/api/SearchPayload";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";
import smallQuantityTicketService from "../../services/smallQuantityTicketService";
import contractorCompanyService from "../../services/contractorCompanyService";
import storageAreaService from "../../services/storageAreaService";
import StockpileCell from "./stockpile-cell";
import { history } from "../..";
import PageHero from "../shared/page-hero";
import SharedButton from "../shared/Button";
import InputSubtitle from "../shared/input-subtitle";
import { isAdmin, a11yProps, PaymentMethods } from "../../utils/constants";
import { TabPanel } from "../shared/tab-panel";
import {
  validateTicketRange,
  createDefaultSearchPayload,
  timeColumn,
  latestReceivedFirst,
} from "../../utils/ticket-helpers";
import { formatTicketNumber } from "../../utils/parser";
import { isDateEqualTo } from "../../utils/date-helper";
import { getCompactTruncateStyle } from "../../utils/table-helpers";
import TicketPill from "../shared/ticket-pill";
import useWindowSize from "../../hooks/useWindowSize";
import useFocusSearch from "../../hooks/useFocusSearch";
import {
  ActionIconGroup,
  StatusIconGroup,
  ReceivedIcon,
  EditIcon,
  DigitalTicketIcon,
  TicketLogsIcon,
  ReceiveIcon,
  SoilFLOIcon,
  SpinningRefreshIcon,
  ViewHaulageRecordIcon,
} from "../shared/icons";
import ReceiveTicketDialog from "./receive-ticket-dialog";
import ConfirmGenerateTicketsDialog from "../shared/dialog/confirm-generate-tickets";
import GenerateTicketsProgressDialog from "../shared/dialog/generate-tickets-progress";
import PriceField from "../shared/price-field";
import FormSelectSearchField from "../shared/form-select-search-field";
import CheckBulkTicketsDialog from "../shared/dialog/check-bulk-tickets";
import { SmallQtyBulkUpdatePayload } from "../../interface/api/SmallQtyBulkUpdatePayload";
import { useBulkUpdateFlow } from "../../hooks/useBulkUpdateFlow";
import ticketService from "../../services/ticketService";
import contractorProjectService from "../../services/contractorProjectService";
import transportCompanyService from "../../services/transportCompanyService";
import { TransportCompany } from "../../interface/api/TransportCompany";
import { Project } from "../../interface/api/Project";
import { Load } from "../../interface/api/soilflo/Load";
import SoilFLOOverrideToast from "../shared/soilflo-override-toast";

const useStyles = makeStyles({
  metricContainer: { padding: "15px", marginBottom: "10px" },
  metricText: { marginTop: "8px" },
});

const SmallQuantityDashboardPanel = () => {
  enum TabValues {
    Search,
    Generate,
    Export,
    Update,
    Clear,
    // Appended so the existing tab indices don't shift.
    Ranges,
  }

  const classes = useStyles();
  const [value, setValue] = useState(TabValues.Search);
  const user = useAppSelector(getCurrentUser);
  const tickets = useAppSelector(getSmallQuantityTickets);
  const dispatch = useAppDispatch();
  const { generateMax, exportMax } = useTicketRangeLimits();
  const [pending, setPending] = useState(false);
  // Range awaiting confirmation / currently generating; drives both dialogs.
  const [generateRange, setGenerateRange] = useState<{ from: number; to: number } | null>(null);
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  // Bumped after a successful generate so the Ranges tab reflects it.
  const [rangesRefresh, setRangesRefresh] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTicketNumber, setSearchTicketNumber] = useState("");
  const [searchTruckNumber, setSearchTruckNumber] = useState("");
  const [searchLicensePlate, setSearchLicensePlate] = useState("");
  const [generateFrom, setGenerateFrom] = useState("");
  const [generateTo, setGenerateTo] = useState("");
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveTicket, setReceiveTicket] = useState<SmallQuantityTicket | null>(null);
  const [soilFLOLoadData, setSoilFLOLoadData] = useState<Load | null>(null);
  const [isSoilFLOGenerate, setIsSoilFLOGenerate] = useState(false);
  const [showActive, setShowActive] = useState(false);
  const [showReceived, setShowReceived] = useState(false);
  // Today's active/received loads fetched server-side (full table, not the
  // latest-2000 slice) so the toggles and their counts are complete.
  const [activeTodayTickets, setActiveTodayTickets] = useState<SmallQuantityTicket[] | null>(null);
  const [receivedTodayTickets, setReceivedTodayTickets] = useState<SmallQuantityTicket[] | null>(null);
  const { isMobile, windowSize } = useWindowSize();
  const isCompact = Number(windowSize.width) <= 1200;
  const { doFocusSearchInput } = useFocusSearch("ticketNumber");

  // Export tab state
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");

  // Contractors
  const [contractors, setContractors] = useState<
    { value: number; label: string }[]
  >([]);

  // Transport companies
  const [transportCompanies, setTransportCompanies] = useState<
    { value: number; label: string }[]
  >([]);

  // Stockpiles (for the Stockpile column — resolve id -> name/status)
  const [stockpileMap, setStockpileMap] = useState<Map<number, Stockpile>>(
    new Map()
  );

  const {
    checkDialogOpen,
    checkResponse,
    onUpdate,
    onConfirmUpdate,
    onCloseCheckDialog,
  } = useBulkUpdateFlow<SmallQtyBulkUpdatePayload>({
    service: smallQuantityTicketService,
    buildPayload: (values) => ({
      from: Number(values.from),
      to: Number(values.to),
      contractorCompanyId: values.contractorCompanyId,
      contractorCompanyName: values.contractorCompanyName,
      jobSite: values.jobSite || null,
      haulageCompanyName: values.haulageCompanyName || null,
      transportCompanyId: values.transportCompanyId || null,
      paymentMethod: values.paymentMethod || null,
      salePrice: values.salePrice ? parseFloat(values.salePrice) : null,
      isSoilFLOLoad: values.isSoilFLOLoad,
      forceUpdate: values.forceUpdate,
      projectId: values.projectId || null,
    }),
    onSuccess: () => loadTickets(),
    setPending,
  });

  useEffect(() => {
    loadTickets();
    contractorCompanyService.getContractorCompanies().then((response) => {
      if (response.ok && response.data) {
        const data = response.data as ContractorCompaniesWithThresholdsResponse;
        const companies = data.contractorCompanies.filter(
          (c) => c.isSmallQuantity
        );
        setContractors(
          companies.map((c) => ({ value: c.id, label: c.name }))
        );
      }
    });
    loadStockpiles();
    transportCompanyService.getTransportCompanies().then((response) => {
      if (response.ok && response.data) {
        const companies = response.data as TransportCompany[];
        setTransportCompanies(
          companies.map((c) => ({ value: c.id, label: c.name }))
        );
      }
    });
  }, []);

  // Refresh stockpile name/status/count map. Called on mount and once after
  // each receive — a single lightweight call (no polling).
  const loadStockpiles = () => {
    storageAreaService.getStockpiles().then((response) => {
      if (response.ok && response.data) {
        const stockpiles = response.data as Stockpile[];
        setStockpileMap(new Map(stockpiles.map((s) => [s.id, s])));
      }
    });
  };

  const loadTickets = async () => {
    setPending(true);
    setRefreshing(true);
    const [latest, active, received] = await Promise.all([
      smallQuantityTicketService.getLatest(),
      smallQuantityTicketService.getActiveToday(),
      smallQuantityTicketService.getReceivedToday(),
    ]);
    if (latest.ok && latest.data) {
      dispatch(setSmallQuantityTickets({ tickets: latest.data as SmallQuantityTicket[] }));
    }
    if (active.ok && active.data) setActiveTodayTickets(active.data as SmallQuantityTicket[]);
    if (received.ok && received.data) setReceivedTodayTickets(received.data as SmallQuantityTicket[]);
    setPending(false);
    setRefreshing(false);
  };

  const searchTickets = async (payload: SearchPayload) => {
    setPending(true);
    const response: ApiResponse<any> =
      await smallQuantityTicketService.searchTickets(payload);
    if (response.ok && response.data) {
      dispatch(setSmallQuantityTickets({ tickets: response.data }));
    }
    setPending(false);
  };

  const debouncedTicketSearch = useCallback(
    debounce((val: string) => {
      if (val) {
        searchTickets({
          ...createDefaultSearchPayload(val),
          truckNumber: searchTruckNumber,
          truckLicensePlate: searchLicensePlate,
        });
      }
    }, 500),
    [searchTruckNumber, searchLicensePlate]
  );

  const onSearchTicketNumberChange = (val: string) => {
    setSearchTicketNumber(val);
    if (!val && !searchTruckNumber && !searchLicensePlate) {
      loadTickets();
      return;
    }
    if (val) debouncedTicketSearch(val);
  };

  const onSearch = () => {
    if (!searchTicketNumber && !searchTruckNumber && !searchLicensePlate) {
      loadTickets();
      return;
    }
    searchTickets({
      ...createDefaultSearchPayload(searchTicketNumber),
      truckNumber: searchTruckNumber,
      truckLicensePlate: searchLicensePlate,
    });
  };

  // Validate, then either confirm or run straight away. runGenerate does the work
  // so the confirmation dialog can call it directly.
  const onGenerate = () => {
    const range = validateTicketRange(generateFrom, generateTo, generateMax);
    if (!range) return;
    setGenerateRange(range);
    if (shouldConfirmBatch(range.from, range.to)) {
      setConfirmGenerateOpen(true);
      return;
    }
    runGenerate(range);
  };

  const runGenerate = async (range: { from: number; to: number }) => {
    setConfirmGenerateOpen(false);
    setPending(true);
    const response: ApiResponse<any> =
      await smallQuantityTicketService.generateTickets({
        from: range.from,
        to: range.to,
        isSoilFLOTicket: isSoilFLOGenerate,
        email: "",
      });
    if (response.ok && response.data) {
      if (response.data.success) {
        toast.success(`Tickets ${range.from}-${range.to} generated`);
        loadTickets();
        setRangesRefresh((n) => n + 1);
      } else {
        toast.error(response.data.error);
      }
    } else {
      toast.error("Failed to generate tickets");
    }
    setPending(false);
    setGenerateRange(null);
  };

  const onClear = async (values: any, resetForm: () => void) => {
    const range = validateTicketRange(values.from, values.to, BulkOperationMaxRange);
    if (!range) return;
    if (!values.clearAll && !values.clearContractor && !values.clearJobSite && !values.clearHaulageCompany && !values.clearPaymentMethod && !values.clearSalePrice) {
      toast.error("Select at least one field to clear");
      return;
    }
    setPending(true);
    const response: ApiResponse<any> =
      await smallQuantityTicketService.bulkClear({
        from: range.from,
        to: range.to,
        clearAll: values.clearAll,
        clearContractor: values.clearContractor,
        clearJobSite: values.clearJobSite,
        clearHaulageCompany: values.clearHaulageCompany,
        clearPaymentMethod: values.clearPaymentMethod,
        clearSalePrice: values.clearSalePrice,
      });
    if (response.ok) {
      toast.success(`${response.data.cleared} ticket(s) cleared`);
      resetForm();
      loadTickets();
    } else {
      toast.error("Failed to clear tickets");
    }
    setPending(false);
  };

  const onExport = async () => {
    const range = validateTicketRange(exportFrom, exportTo, exportMax);
    if (!range) return;

    const email = user ? user.username : "";
    if (!email) {
      toast.error("No email found for current user");
      return;
    }

    setPending(true);
    const response: ApiResponse<any> =
      await smallQuantityTicketService.exportTicketsExcel({
        from: range.from,
        to: range.to,
        email,
      });
    if (response.ok) {
      toast.success(
        `Export queued — you'll receive it at ${email} when it's ready.`
      );
    } else {
      const data = response.data as string;
      toast.error(data || "Failed to export tickets");
    }
    setPending(false);
  };

  const onReceiveClick = async (ticket: SmallQuantityTicket) => {
    // Fetch fresh ticket data
    const freshResponse = await smallQuantityTicketService.getTicket(ticket.id);
    if (!freshResponse.ok) {
      toast.error("Failed to get latest ticket data");
      return;
    }
    const freshTicket = freshResponse.data as SmallQuantityTicket;

    if (freshTicket.isSoilFLOLoad) {
      ticketService.getLoad(freshTicket.ticketNumber.toString()).then((response) => {
        if (response.ok) {
          const data = response.data as Load[];
          if (data && data.length > 0) {
            const load = data.sort((a, b) => b.id - a.id)[0];
            setSoilFLOLoadData(load);

            try {
              const ticketWithSoilFLOData = { ...freshTicket };
              ticketWithSoilFLOData.jobSite = load.sourceSite?.name || "Unknown";
              ticketWithSoilFLOData.contractorCompanyName =
                load.sourceSite?.contractingcompany?.name || "Unknown";
              ticketWithSoilFLOData.haulageCompanyName =
                load.truck?.global_truck?.hauler?.name || "Unknown";
              ticketWithSoilFLOData.truckNumber =
                load.truck?.global_truck?.number || "Unknown";
              ticketWithSoilFLOData.truckLicensePlate =
                load.truck?.global_truck?.license || "Unknown";
              // No "Unknown" placeholder: this is saved as the driver of record.
              ticketWithSoilFLOData.truckDriverName =
                load.truck?.driverName || freshTicket.truckDriverName;
              ticketWithSoilFLOData.timeLoaded = load.dispatchedTime;

              setReceiveTicket(ticketWithSoilFLOData);
              setReceiveDialogOpen(true);
            } catch (e) {
              toast.error("SoilFLO data is not correct");
            }
          } else {
            toast.error(
              <SoilFLOOverrideToast
                ticketNumber={freshTicket.ticketNumber}
                onOverride={() => {
                  setSoilFLOLoadData(null);
                  setReceiveTicket({ ...freshTicket, isSoilFLOLoad: false });
                  setReceiveDialogOpen(true);
                }}
              />,
              { autoClose: false }
            );
          }
        } else {
          toast.error("Failed to pull SoilFLO data");
        }
      });
    } else {
      setSoilFLOLoadData(null);
      setReceiveTicket(freshTicket);
      setReceiveDialogOpen(true);
    }
  };

  const onConfirmReceive = async (editedTicket: SmallQuantityTicket) => {
    // One request: the server applies the dialog's edits and receives the load, and
    // never touches Time Loaded (see SubStreamReceivePayload).
    const response: ApiResponse<any> =
      await smallQuantityTicketService.receiveTicket(editedTicket);
    if (!response.ok) {
      toast.error("Failed to receive ticket");
      return;
    }
    const data = response.data as any;
    if (data.success === false) {
      toast.error("Failed to update SoilFLO");
    } else {
      toast.success("Ticket received");
    }
    setReceiveDialogOpen(false);
    setReceiveTicket(null);
    setSoilFLOLoadData(null);
    setSearchTicketNumber("");
    setSearchTruckNumber("");
    setSearchLicensePlate("");
    loadTickets();
    loadStockpiles();
    doFocusSearchInput();
  };

  const totalLoadedToday = useMemo(
    () => tickets.filter((t) => isDateEqualTo(t.timeLoaded, new Date())).length,
    [tickets]
  );
  const receivedToday = useMemo(
    () =>
      receivedTodayTickets?.length ??
      tickets.filter((t) => t.isReceived && isDateEqualTo(t.timeReceived, new Date())).length,
    [receivedTodayTickets, tickets]
  );
  const uniqueContractorsToday = useMemo(
    () =>
      new Set(
        tickets
          .filter((t) => (isDateEqualTo(t.timeLoaded, new Date()) || isDateEqualTo(t.timeReceived, new Date())) && t.contractorCompanyName)
          .map((t) => t.contractorCompanyName)
      ).size,
    [tickets]
  );

  const activeCount = useMemo(
    () =>
      activeTodayTickets?.length ??
      tickets.filter((t) => isDateEqualTo(t.timeLoaded, new Date()) && t.isReceived === false).length,
    [activeTodayTickets, tickets]
  );
  // Received shows tickets received today, regardless of the time-loaded date.
  const receivedCount = useMemo(
    () =>
      receivedTodayTickets?.length ??
      tickets.filter((t) => t.isReceived && isDateEqualTo(t.timeReceived, new Date())).length,
    [receivedTodayTickets, tickets]
  );

  // Toggles draw from the server-fetched full-table lists (falling back to the
  // in-memory slice only until those lists arrive).
  const filteredTickets = useMemo(() => {
    if (showActive)
      return (
        activeTodayTickets ??
        tickets.filter((t) => isDateEqualTo(t.timeLoaded, new Date()) && t.isReceived === false)
      );
    if (showReceived)
      return latestReceivedFirst(
        receivedTodayTickets ??
          tickets.filter((t) => t.isReceived && isDateEqualTo(t.timeReceived, new Date())),
        (t) => t.timeReceived
      );
    return tickets;
  }, [tickets, activeTodayTickets, receivedTodayTickets, showActive, showReceived]);

  const onToggleShowActive = () => {
    setShowActive(!showActive);
    setShowReceived(false);
  };

  const onToggleShowReceived = () => {
    setShowReceived(!showReceived);
    setShowActive(false);
  };

  const truncateStyle = getCompactTruncateStyle(isCompact);

  const columns: TableColumn<SmallQuantityTicket>[] = [
    {
      name: "",
      width: isCompact ? "100px" : "125px",
      cell: (row) => {
        if (row.isReceived) return <></>;
        return (
          <ReceiveIcon
            isMobile={isCompact}
            onClick={() => onReceiveClick(row)}
          />
        );
      },
    },
    {
      name: "Ticket #",
      sortable: true,
      sortFunction: (a, b) => a.ticketNumber - b.ticketNumber,
      width: isCompact ? "110px" : "140px",
      cell: (row) => (
        <TicketPill
          ticketNumber={`[${row.prefix}] ${formatTicketNumber(row.ticketNumber)}`}
          copyValue={formatTicketNumber(row.ticketNumber)}
          isCompact={isCompact}
        />
      ),
    },
    showReceived
      ? timeColumn<SmallQuantityTicket>("Time Received", (row) => row.timeReceived, isCompact)
      : timeColumn<SmallQuantityTicket>("Time Loaded", (row) => row.timeLoaded, isCompact),
    {
      name: "Job Site",
      selector: (row) => row.jobSite ?? "",
      sortable: true,
      maxWidth: isCompact ? "110px" : undefined,
      style: truncateStyle,
    },
    {
      name: "Contractor",
      selector: (row) => row.contractorCompanyName ?? "",
      sortable: true,
      maxWidth: isCompact ? "110px" : undefined,
      style: truncateStyle,
    },
    {
      name: "Truck #",
      selector: (row) => row.truckNumber ?? "",
      sortable: true,
      maxWidth: isCompact ? "100px" : undefined,
      style: truncateStyle,
    },
    {
      name: "License Plate",
      selector: (row) => row.truckLicensePlate ?? "",
      sortable: true,
      maxWidth: isCompact ? "100px" : undefined,
      style: truncateStyle,
    },
    {
      name: "Stockpile",
      width: isCompact ? "150px" : "190px",
      cell: (row) => (
        <StockpileCell
          stockpile={
            row.stockpileId != null
              ? stockpileMap.get(row.stockpileId)
              : undefined
          }
        />
      ),
    },
    {
      name: "",
      width: "40px",
      cell: (row) => row.isSoilFLOLoad ? <SoilFLOIcon /> : null,
    },
    {
      name: "Status",
      width: "80px",
      cell: (row) => (
        <StatusIconGroup>
          {row.isReceived && <ReceivedIcon />}
        </StatusIconGroup>
      ),
    },
    {
      name: "Actions",
      // Widened for the fourth icon (haulage record); keeps the same per-icon spacing.
      width: isCompact ? "155px" : "195px",
      cell: (row) => (
        <ActionIconGroup>
          <EditIcon
            onClick={() => history.push(`/small-quantity/${row.id}`)}
          />
          <DigitalTicketIcon
            onClick={() =>
              history.push(`/small-quantity-digital-ticket/${row.id}`)
            }
          />
          <TicketLogsIcon
            onClick={() => history.push(`/small-quantity-ticket-log/${row.id}`)}
          />
          <ViewHaulageRecordIcon
            onClick={() =>
              history.push(`/small-quantity-haulage-record/${row.id}`)
            }
          />
        </ActionIconGroup>
      ),
    },
  ];

  return (
    <Container maxWidth="xl">
      <PageHero
        title="Small Quantities"
        subtitle="Manage small quantity soil tickets."
        showBackButton={false}
      />
      <div>
        <AppBar position="static" color="default">
        <Tabs
          value={value}
          onChange={(_, newValue) => setValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="on"
          aria-label="scrollable tabs"
        >
          <Tab label="Search" {...a11yProps(0)} />
          {user &&
            isAdmin(user.roleId) && [
              <Tab key="generate" label="Generate" {...a11yProps(1)} />,
              <Tab key="export" label="Export" {...a11yProps(2)} />,
              <Tab key="update" label="Update" {...a11yProps(3)} />,
              <Tab key="clear" label="Clear" {...a11yProps(4)} />,
              <Tab key="ranges" label="Ranges" {...a11yProps(5)} />,
            ]}
        </Tabs>
        </AppBar>
        <TabPanel value={value} index={TabValues.Search}>
          <Box sx={{ marginY: 8 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  label="Ticket Number"
                  name="ticketNumber"
                  value={searchTicketNumber}
                  onChange={(e) =>
                    onSearchTicketNumberChange(e.target.value)
                  }
                  fullWidth
                  autoFocus
                />
                <InputSubtitle text="*Auto Search Enabled" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  label="Truck Number"
                  name="truckNumber"
                  value={searchTruckNumber}
                  onChange={(e) => setSearchTruckNumber(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  label="License Plate"
                  name="licensePlate"
                  value={searchLicensePlate}
                  onChange={(e) => setSearchLicensePlate(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <SharedButton
                  label="Search"
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={onSearch}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SharedButton
                  label="Clear"
                  type="button"
                  variant="contained"
                  color="default"
                  onClick={() => {
                    setSearchTicketNumber("");
                    setSearchTruckNumber("");
                    setSearchLicensePlate("");
                    loadTickets();
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
        <TabPanel value={value} index={TabValues.Generate}>
          <Box sx={{ marginY: 8 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="From Ticket Number"
                  value={generateFrom}
                  onChange={(e) => setGenerateFrom(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="To Ticket Number"
                  value={generateTo}
                  onChange={(e) => setGenerateTo(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSoilFLOGenerate}
                      onChange={(e) => setIsSoilFLOGenerate(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="SoilFLO Tickets"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SharedButton
                  label={pending ? "Generating..." : "Generate Tickets"}
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={onGenerate}
                  disabled={pending}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
        <TabPanel value={value} index={TabValues.Update}>
          <Box sx={{ marginY: 8 }}>
            <Formik
              initialValues={{
                from: "",
                to: "",
                contractorCompanyId: null as number | null,
                contractorCompanyName: null as string | null,
                projectId: null as number | null,
                jobSite: "",
                haulageCompanyName: "",
                transportCompanyId: null as number | null,
                paymentMethod: "",
                salePrice: "",
                isSoilFLOLoad: null as boolean | null,
                forceUpdate: false,
                _projects: [] as Project[],
              }}
              onSubmit={(values, { resetForm }) => onUpdate(values, resetForm)}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="From Ticket Number"
                          value={values.from}
                          onChange={(e) => setFieldValue("from", e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="To Ticket Number"
                          value={values.to}
                          onChange={(e) => setFieldValue("to", e.target.value)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormSelectSearchField
                          name="contractorCompanyId"
                          placeholder="Contractor"
                          options={contractors}
                          value={
                            values.contractorCompanyId
                              ? contractors.find((c) => c.value === values.contractorCompanyId) ?? null
                              : null
                          }
                          onChange={(option: any) => {
                            if (option) {
                              setFieldValue("contractorCompanyId", option.value);
                              setFieldValue("contractorCompanyName", option.label);
                              setFieldValue("projectId", null);
                              contractorProjectService
                                .getContractorProjects(option.value)
                                .then((response) => {
                                  if (response.ok) {
                                    setFieldValue("_projects", response.data as Project[]);
                                  }
                                });
                            } else {
                              setFieldValue("contractorCompanyId", null);
                              setFieldValue("contractorCompanyName", null);
                              setFieldValue("projectId", null);
                              setFieldValue("_projects", []);
                            }
                          }}
                          isClearable
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Project</InputLabel>
                          <Select
                            label="Project"
                            value={values.projectId ?? ""}
                            onChange={(e) => {
                              const projId = e.target.value as number;
                              setFieldValue("projectId", projId || null);
                              const proj = values._projects.find((p) => p.id === projId);
                              if (proj) {
                                setFieldValue("jobSite", proj.jobSite || "");
                                if (proj.salePrice) {
                                  setFieldValue("salePrice", String(proj.salePrice));
                                }
                                setFieldValue("isSoilFLOLoad", proj.isSoilFLOProject || null);
                              }
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {values._projects.map((p) => (
                              <MenuItem key={p.id} value={p.id}>
                                {p.projectId} — {p.jobSite}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Job Site"
                          value={values.jobSite}
                          onChange={(e) => setFieldValue("jobSite", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormSelectSearchField
                          name="haulageCompanyName"
                          placeholder="Haulage Company"
                          options={transportCompanies}
                          value={
                            values.haulageCompanyName
                              ? transportCompanies.find((c) => c.label === values.haulageCompanyName) ?? null
                              : null
                          }
                          onChange={(option: any) => {
                            setFieldValue("haulageCompanyName", option ? option.label : null);
                            setFieldValue("transportCompanyId", option ? option.value : null);
                          }}
                          isClearable
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Payment Method</InputLabel>
                          <Select
                            label="Payment Method"
                            value={values.paymentMethod}
                            onChange={(e) =>
                              setFieldValue("paymentMethod", e.target.value as string)
                            }
                          >
                            <MenuItem value="">None</MenuItem>
                            {PaymentMethods.map((m) => (
                              <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <PriceField
                          label="Sale Price"
                          value={values.salePrice}
                          onChange={(val) => setFieldValue("salePrice", val ?? "")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isSoilFLOLoad === true}
                              onChange={(e) => setFieldValue("isSoilFLOLoad", e.target.checked ? true : null)}
                              color="primary"
                            />
                          }
                          label="SoilFLO"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {/* Also updates received tickets, as the main stream's Force Update does. */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.forceUpdate}
                              onChange={() => setFieldValue("forceUpdate", !values.forceUpdate)}
                              color="primary"
                            />
                          }
                          label="Force Update"
                        />
                      </Grid>
                    </Grid>
                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <SharedButton
                            label={pending ? "Updating..." : "Update"}
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={pending}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </form>
                  <CheckBulkTicketsDialog
                    open={checkDialogOpen}
                    onClose={onCloseCheckDialog}
                    onConfirmSubmit={onConfirmUpdate}
                    response={checkResponse}
                  />
                </>
              )}
            </Formik>
          </Box>
        </TabPanel>
        <TabPanel value={value} index={TabValues.Clear}>
          <Box sx={{ marginY: 8 }}>
            <Formik
              initialValues={{
                from: "",
                to: "",
                clearAll: false,
                clearContractor: false,
                clearJobSite: false,
                clearHaulageCompany: false,
                clearPaymentMethod: false,
                clearSalePrice: false,
              }}
              onSubmit={(values, { resetForm }) => onClear(values, resetForm)}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="From Ticket Number"
                        value={values.from}
                        onChange={(e) => setFieldValue("from", e.target.value)}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="To Ticket Number"
                        value={values.to}
                        onChange={(e) => setFieldValue("to", e.target.value)}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        style={{
                          border: "2px solid #ccc",
                          borderRadius: 4,
                          padding: "8px 16px",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.clearAll}
                              onChange={(e) => setFieldValue("clearAll", e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Clear All"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearContractor}
                            onChange={(e) => setFieldValue("clearContractor", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Contractor"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearJobSite}
                            onChange={(e) => setFieldValue("clearJobSite", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Job Site"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearHaulageCompany}
                            onChange={(e) => setFieldValue("clearHaulageCompany", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Haulage Company"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearPaymentMethod}
                            onChange={(e) => setFieldValue("clearPaymentMethod", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Payment Method"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.clearAll || values.clearSalePrice}
                            onChange={(e) => setFieldValue("clearSalePrice", e.target.checked)}
                            disabled={values.clearAll}
                            color="primary"
                          />
                        }
                        label="Sale Price"
                      />
                    </Grid>
                  </Grid>
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <SharedButton
                          label={pending ? "Clearing..." : "Clear"}
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={pending}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </form>
              )}
            </Formik>
          </Box>
        </TabPanel>
        <TabPanel value={value} index={TabValues.Export}>
          <Box sx={{ marginY: 8 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="From Ticket Number"
                  value={exportFrom}
                  onChange={(e) => setExportFrom(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="To Ticket Number"
                  value={exportTo}
                  onChange={(e) => setExportTo(e.target.value)}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SharedButton
                  label={pending ? "Exporting..." : "Export"}
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={onExport}
                  disabled={pending}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
        <TabPanel value={value} index={TabValues.Ranges}>
          <Box sx={{ marginY: 8 }}>
            <TicketRangesPanel
              stream="small-quantity"
              refreshToken={rangesRefresh}
              onUseRange={(from, to) => {
                setGenerateFrom(String(from));
                setGenerateTo(String(to));
                setValue(TabValues.Generate);
              }}
            />
          </Box>
        </TabPanel>
        {/* Daily metrics and the ticket listing are irrelevant on the Ranges tab. */}
        {value !== TabValues.Ranges && (
          <>
        <Paper variant="outlined" className={classes.metricContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} className={classes.metricText}>
              <Box display="flex" alignItems="center" mb={1}>
                <LocalShippingIcon style={{ marginRight: 6 }} />
                <Typography
                  variant="h6"
                  style={{ fontWeight: 600, marginRight: 6 }}
                >
                  Daily Trucks
                </Typography>
                <Tooltip
                  title="Daily summary of small quantity tickets for today."
                  placement="top"
                  arrow
                >
                  <InfoIcon
                    fontSize="small"
                    color="action"
                    style={{ cursor: "pointer" }}
                  />
                </Tooltip>
              </Box>
              <DailyMetrics
                metrics={[
                  {
                    icon: <LocalShippingIcon fontSize="small" />,
                    value: totalLoadedToday,
                    label: "Loaded",
                  },
                  {
                    icon: <ReceiptIcon fontSize="small" />,
                    value: receivedToday,
                    label: "Received",
                  },
                  {
                    icon: <BusinessIcon fontSize="small" />,
                    value: uniqueContractorsToday,
                    label: "Contractor(s)",
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" justifyContent={isMobile ? "space-evenly" : "flex-end"} flexWrap="wrap" style={{ gap: 4, width: "100%" }}>
                  <Chip
                    label={`Active ${activeCount}`}
                    color={showActive ? "primary" : "default"}
                    onClick={onToggleShowActive}
                    variant={showActive ? "default" : "outlined"}
                    size="small"
                    style={isMobile ? { flex: 1 } : undefined}
                  />
                  <Chip
                    label={`Received ${receivedCount}`}
                    color={showReceived ? "primary" : "default"}
                    onClick={onToggleShowReceived}
                    variant={showReceived ? "default" : "outlined"}
                    size="small"
                    style={isMobile ? { flex: 1 } : undefined}
                  />
                <Box ml={1}>
                  <SpinningRefreshIcon onClick={loadTickets} refreshing={refreshing} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <PaginatedTableTop>
          <DataTable
            className={paginationTopClassName}
            customStyles={tableCustomStyles}
            fixedHeader
            striped
            columns={columns}
            data={filteredTickets}
            pagination
            paginationPerPage={50}
            paginationRowsPerPageOptions={[50, 100, 150, 200]}
            progressPending={pending}
            progressComponent={<CircularProgress style={{ margin: 20 }} />}
            highlightOnHover
            responsive
          />
        </PaginatedTableTop>
          </>
        )}
      </div>
      {generateRange && (
        <ConfirmGenerateTicketsDialog
          open={confirmGenerateOpen}
          from={generateRange.from}
          to={generateRange.to}
          onClose={() => {
            setConfirmGenerateOpen(false);
            setGenerateRange(null);
          }}
          onConfirm={() => runGenerate(generateRange)}
        />
      )}
      {generateRange && (
        <GenerateTicketsProgressDialog
          open={pending && !confirmGenerateOpen}
          from={generateRange.from}
          to={generateRange.to}
        />
      )}
      <ReceiveTicketDialog
        open={receiveDialogOpen}
        ticket={receiveTicket}
        onClose={() => {
          setReceiveDialogOpen(false);
          setReceiveTicket(null);
        }}
        onReceive={onConfirmReceive}
      />
    </Container>
  );
};

export default SmallQuantityDashboardPanel;
